import axios from "axios";

import { ConnectionHistoryChartInfo, Device, ScanResponse } from "@/models";
import { isAndroid } from "@/plugins/native-app/android";
import router from "@/router";
import { getToken } from "./auth";
import { SESSION_TIMEOUT } from "./constants";
import EventBus from "./event-bus";

export const BASE_URL = `${window.location.origin}/api/v1`;
export const WS_BASE_URL = `wss://${window.location.host}/api/v1`;

// Android devices don't have a way to configure the default timeout, so set the timeout for Axios for Android devices
if (isAndroid()) {
  axios.defaults.timeout = 20 * 1000;
}

// ENOTFOUND happens when disconnecting from wifi
// ETIMOUT happens when physically unplugging the internet cable
const CONNECTION_ERROR_REGEX = /.+(ENOTFOUND|ETIMEOUT).+/i;
function isNetworkError(error: any) {
  // If there isn't a response, that can signify a network error
  if (!error?.response) return true;

  // If there is a response but the status was 500 with a specific error message, that can also signify a network error
  if (error.response.status === 500) {
    return CONNECTION_ERROR_REGEX.test(error.response.data || "");
  }

  return false;
}

// Automatically inject the auth token on API requests
axios.interceptors.request.use(
  request => {
    const token = getToken();

    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    return request;
  },
  error => Promise.reject(error)
);

axios.interceptors.response.use(
  // Automatically return the response data instead of making each method return response.data
  response => response.data,

  error => {
    // In the case of network failure, the response may be undefined so default to an empty object
    const response = error?.response || {};
    if (response.status === 401) {
      EventBus.$emit(SESSION_TIMEOUT);
    }
    if (response.status === 403) {
      router.push("/unauthorized");
      return Promise.resolve();
    }

    response.isNetworkError = isNetworkError(error);

    // Have Axios return the response instead of the error
    return Promise.reject(response);
  }
);

export class CoreApi {
  login(username: string, password: string, isMobileUser: boolean): Promise<any> {
    const url = `${BASE_URL}/login`;
    return axios.post(url, { username, password, isMobileUser });
  }

  logout(): Promise<void> {
    const url = `${BASE_URL}/logout`;
    return axios.post(url, {});
  }

  getConnections(): Promise<any> {
    const url = `${BASE_URL}/connections`;
    return axios.get(url);
  }

  getEvents(from: string, to: string): Promise<any> {
    const encFrom = encodeURIComponent(from);
    const encTo = encodeURIComponent(to);
    const url = `${BASE_URL}/events?from=${encFrom}&to=${encTo}`;

    return axios.get(url);
  }

  getConnection(id: string): Promise<any> {
    const encID = encodeURIComponent(id);
    const url = `${BASE_URL}/connections/${encID}`;
    return axios.get(url);
  }

  getStream(id: string) {
    const encID = encodeURIComponent(id);
    return new WebSocket(`${WS_BASE_URL}/connections/${encID}/ws?token=${getToken()}`);
  }

  getLocations(): Promise<any> {
    const url = `${BASE_URL}/locations`;
    return axios.get(url);
  }

  getDevices(): Promise<Device[]> {
    const url = `${BASE_URL}/devices`;
    return axios.get(url);
  }

  updateDeviceName(device: Device): Promise<Device> {
    const url = `${BASE_URL}/device`;
    return axios.put(url, device);
  }

  getLocation(ip: string): Promise<any> {
    const encIp = encodeURIComponent(ip);
    const url = `${BASE_URL}/locations/${encIp}`;
    return axios.get(url);
  }

  updateLocation(location: any): Promise<any> {
    const encIP = encodeURIComponent(location.ip);
    const url = `${BASE_URL}/locations/${encIP}`;
    return axios.put(url, location);
  }

  getSettings(): Promise<any> {
    const url = `${BASE_URL}/settings`;
    return axios.get(url);
  }

  updateSettings(settings: any): Promise<any> {
    const url = `${BASE_URL}/settings`;
    return axios.put(url, settings);
  }

  createLocation(location: any): Promise<any> {
    const url = `${BASE_URL}/locations`;
    return axios.post(url, location);
  }

  deleteLocation(ip: string): Promise<any> {
    const encIp = encodeURIComponent(ip);
    const url = `${BASE_URL}/locations/${encIp}`;
    return axios.delete(url);
  }

  getInfo(): Promise<any> {
    const url = `${BASE_URL}/info`;
    return axios.get(url);
  }

  getBuildInfo(): Promise<any> {
    const url = `${BASE_URL}/buildInfo`;
    return axios.get(url);
  }

  getPatient(id: string): Promise<any> {
    const encID = encodeURIComponent(id);
    const url = `${BASE_URL}/patients/${encID}`;
    return axios.get(url);
  }

  getDevice(serial: string): Promise<any> {
    const encSerial = encodeURIComponent(serial);
    const url = `${BASE_URL}/devices/${encSerial}`;
    return axios.get(url);
  }

  associateDevice(serial: string, patientID?: string | null): Promise<any> {
    const url = `${BASE_URL}/associate`;
    return axios.post(url, { patientID, serial });
  }

  sendManualEntryReason(reason: string, id: string): Promise<any> {
    const url = `${BASE_URL}/manualEntry`;
    return axios.post(url, { reason, id });
  }

  getConnectionHistory(from: string, to: string): Promise<ConnectionHistoryChartInfo[]> {
    const encFrom = encodeURIComponent(from);
    const encTo = encodeURIComponent(to);
    const url = `${BASE_URL}/devices/history?from=${encFrom}&to=${encTo}`;

    return axios.get(url);
  }

  scan(barcode: string, isScanned: boolean): Promise<ScanResponse> {
    const url = `${BASE_URL}/scan`;
    return axios.post(url, { barcode, isScanned });
  }
}

export default new CoreApi();
